import React from 'react';
import { Link, useParams } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { services } from '../mock/mockData';
import { SERVICES_KEYWORDS, LANG_LABELS } from '../mock/serviceKeywords';
import { keywordsForService } from '../data/disciplineKeywords';
import { Typewriter, SectionLabel, CTA, FloatingActions } from './Home';
import useSeo from '../hooks/useSeo';
import DotConnect from '../components/DotConnect';
import BlueprintHero from '../components/BlueprintHero';
import ServiceBackdrop from '../components/ServiceBackdrop';
import WhyChooseUs from '../components/WhyChooseUs';
import { useLang } from '../contexts/LanguageContext';

export const ServicesIndex = () => {
  useSeo({
    title: 'All Services',
    description: 'Explore our full catalog of engineering services — land surveying, BIM, MEP, structural, civil and CAD drafting — backed by 350+ engineers and 20+ years of global delivery.',
    path: '/services'
  });
  const { tt } = useLang();
  const grouped = services.reduce((acc, s) => {
    acc[s.category] = acc[s.category] || [];
    acc[s.category].push(s);
    return acc;
  }, {});
  const totalSubServices = SERVICES_KEYWORDS.reduce((n, s) => n + s.subServices.length, 0);
  const highlights = [
    { icon: 'Zap', label: '24-48hr', sub: 'Typical TAT' },
    { icon: 'ShieldCheck', label: 'ISO 9001:2015', sub: 'Certified QMS' },
    { icon: 'Languages', label: '20 Languages', sub: 'RTL-ready SEO' },
    { icon: 'Globe2', label: '40+ Countries', sub: 'Global delivery' },
    { icon: 'Users', label: '350+', sub: 'Multi-disc engineers' },
    { icon: 'TrendingUp', label: 'Save 54%', sub: 'vs in-house cost' }
  ];
  return (
    <>
      <PageHero title="Our" highlight="Services" typewriter={[ 'Engineering', 'Drafting', 'Modeling', 'Surveying' ]} subtitle="Comprehensive engineering capabilities across all disciplines, delivered as integrated solutions from concept to completion." />

      {/* Intro + KPIs */}
      <section className="py-14 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <SectionLabel>One partner. Every discipline.</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0e1116] mt-3 leading-tight">
              {services.length} {tt('expert services.')} <span className="text-green-600">{totalSubServices}+ {tt('sub-services.')}</span> <span className="text-orange-500">{tt('One delivery team.')}</span>
            </h2>
            <p className="text-gray-600 mt-5 leading-relaxed max-w-3xl">
              {tt('From land surveying and BIM modelling to MEP, civil, structural, plant, environmental and process-equipment engineering — every discipline you need to take a project from concept to handover lives under one roof. ISO-certified, NDA-protected, with a 3-stage quality check on every deliverable.')}
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/get-quote" data-testid="services-intro-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors">
                {tt('Get a Free Quote')} <Icons.ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border-2 border-[#0e1116] hover:bg-[#0e1116] hover:text-white text-[#0e1116] font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors">
                {tt('Talk to an Engineer')}
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {highlights.map(h => {
              const Ic = Icons[h.icon] || Icons.Sparkles;
              return (
                <div key={h.label} className="bg-gray-50 hover:bg-gray-50 border border-gray-200 hover:border-green-400 rounded-lg p-4 transition-colors" data-testid={`svc-highlight-${h.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <Ic className="w-6 h-6 text-orange-500" />
                  <div className="font-black text-[#0e1116] mt-2 text-lg leading-none">{tt(h.label)}</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{tt(h.sub)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services by category */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-14" data-testid={`svc-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-1 bg-orange-500"></div>
                  <h2 className="text-2xl md:text-3xl font-black text-[#0e1116]">{tt(cat)}</h2>
                  <span className="text-xs uppercase tracking-wider font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">{list.length} {tt('services')}</span>
                </div>
                <Link to="/get-quote" className="hidden md:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-green-600 hover:text-orange-500">{tt('Quote for this category')} <Icons.ArrowRight className="w-3 h-3" /></Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {list.map((s, i) => {
                  const Ic = Icons[s.icon] || Icons.Box;
                  const accent = i % 2 === 0 ? 'bg-gray-600' : 'bg-orange-500';
                  const kw = SERVICES_KEYWORDS.find(k => k.slug === s.slug);
                  const sample = kw ? kw.subServices.slice(0, 4) : [];
                  return (
                    <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`svc-card-${s.slug}`} className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      <div className="relative h-44 overflow-hidden">
                        <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                        <div className={`absolute -bottom-5 left-5 w-11 h-11 ${accent} rounded shadow-lg flex items-center justify-center`}><Ic className="w-5 h-5 text-white" /></div>
                      </div>
                      <div className="p-6 pt-7">
                        <h3 className="font-bold text-lg text-[#0e1116] group-hover:text-green-600 transition-colors">{tt(s.title)}</h3>
                        <p className="text-gray-500 text-sm mt-2 line-clamp-2">{tt(s.short)}</p>
                        {sample.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-1.5">
                            {sample.map(sub => (
                              <span key={sub} className="text-[10px] uppercase tracking-wider bg-gray-50 text-green-700 border border-gray-200 px-2 py-0.5 rounded">{tt(sub)}</span>
                            ))}
                            {kw && kw.subServices.length > sample.length && (
                              <span className="text-[10px] uppercase tracking-wider bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded">+{kw.subServices.length - sample.length} {tt('more')}</span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-1 mt-4 text-green-600 text-xs font-bold uppercase tracking-wider">
                          {tt('Explore')} <Icons.ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <WhyChooseUs />

      {/* Process strip */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <SectionLabel>How we work</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-black text-[#0e1116]">{tt('Concept →')} <span className="text-green-600">{tt('Delivery')}</span> {tt('in six clear steps')}</h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">{tt('A repeatable, audit-friendly engineering lifecycle backed by 20+ years of practice across 40+ countries.')}</p>
          <Link to="/about" className="inline-flex items-center gap-1 mt-5 text-sm font-bold uppercase tracking-wider text-orange-500 hover:text-green-600">{tt('See our full process')} <Icons.ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </section>

      <CTA />
      <FloatingActions />
    </>
  );
};

export const ServiceDetail = () => {
  const { slug } = useParams();
  const s = services.find(x => x.slug === slug) || services[0];
  const intentKeywords = keywordsForService(s.slug);
  useSeo({
    title: `${s.title} — ${s.category}`,
    description: s.short || `Land Drafters delivers ${s.title} services across 40+ countries with ISO 9001:2015 certified workflows.`,
    path: `/services/${s.slug}`,
    keywords: intentKeywords.length ? intentKeywords : undefined
  });
  const Ic = Icons[s.icon] || Icons.Box;
  const related = services.filter(x => x.category === s.category && x.slug !== s.slug).slice(0, 3);
  const kw = SERVICES_KEYWORDS.find(k => k.slug === s.slug);
  const langCodes = Object.keys(LANG_LABELS);
  const { tt } = useLang();
  return (
    <>
      <section className="relative pt-20 pb-12 md:pt-24 md:pb-16 bg-[#0e1116] text-white overflow-hidden" data-testid="service-hero">
        <DotConnect density={50} color="#94a3b8" lineColor="rgba(148,163,184,0.85)" accentColor="#f97316" maxDistance={130} />
        <ServiceBackdrop slug={s.slug} accent="#94a3b8" accentAlt="#f97316" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(148,163,184,0.2),transparent_60%)] pointer-events-none"></div>
        <div className="relative max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/services" className="inline-flex items-center gap-1 text-green-400 text-xs font-bold uppercase tracking-wider mb-5 hover:text-orange-400" data-testid="back-to-services">
              <Icons.ArrowLeft className="w-3.5 h-3.5" /> All Services
            </Link>
            <div className="inline-flex items-center gap-2 bg-gray-900/50 border border-gray-700/50 text-green-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded mb-4">
              <Ic className="w-4 h-4" /> {s.category}
            </div>
            <h1 className="text-4xl md:text-6xl font-black leading-[1.05]">
              {s.title.split(' ').slice(0, -1).join(' ') || s.title}{' '}
              <span className="text-green-500">{s.title.split(' ').slice(-1)[0]}</span>
            </h1>
            <div className="mt-3 text-lg md:text-2xl font-bold">
              <Typewriter
                words={[
                  'Engineered for Precision',
                  'Built to Global Standards',
                  'Delivered at Scale',
                  'Ready in 24-48 Hours'
                ]}
                className="text-orange-500"
              />
            </div>
            <p className="text-gray-300 mt-6 max-w-xl leading-relaxed">{s.short}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/get-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors" data-testid="hero-quote-btn">
                Request Quote <Icons.ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:border-green-500 hover:text-green-400 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors" data-testid="hero-contact-btn">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="hidden lg:block" data-testid="service-hero-animation">
            <BlueprintHero slug={s.slug} accent="#94a3b8" accentAlt="#f97316" density={22} />
          </div>
        </div>
      </section>

      <section className="py-20 bg-white relative overflow-hidden">
        <ServiceBackdrop slug={s.slug} theme="light" />
        <div className="relative max-w-[1100px] mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <SectionLabel>Overview</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0e1116]">{tt('Precision')} <span className="text-green-600">{tt(s.title)}</span> {tt('Solutions')}</h2>
            <p className="text-gray-600 mt-5 leading-relaxed">
              {tt(`At Land Drafters, we deliver comprehensive ${s.title.toLowerCase()} solutions that meet the highest global engineering standards. Our experienced team uses advanced digital methodologies, industry-leading software, and a globally distributed delivery model to ensure your project is completed accurately, efficiently, and on schedule.`)}
            </p>
            <p className="text-gray-600 mt-4 leading-relaxed">
              {tt('From initial concept and feasibility through detailed design, construction documentation, and project management, we are your dedicated engineering partner. Our ISO 9001:2015 certified processes and 3-stage quality check methodology guarantee error-free deliverables every time.')}
            </p>

            <h3 className="font-black text-xl text-[#0e1116] mt-9 mb-4">{tt('What We Deliver')}</h3>
            <ul className="space-y-3">
              {['Detailed engineering documentation tailored to your project needs','Compliance with global codes and local standards','Coordinated multi-discipline design and clash detection','Fast 24-48 hour turnaround on standard projects','Rush options for critical-path deliverables','Dedicated project manager and engineering team'].map((p) => (
                <li key={p} className="flex gap-3"><Icons.CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" /><span className="text-gray-600 text-sm">{tt(p)}</span></li>
              ))}
            </ul>

            <h3 className="font-black text-xl text-[#0e1116] mt-10 mb-4">{tt('Our Process')}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {['Discovery & scope confirmation','Quote in 24 hours','Engineering execution','QC review & PE stamp','Final delivery & revisions','Ongoing support'].map((p, i) => (
                <div key={p} className="flex items-start gap-3 p-4 bg-gray-50 rounded">
                  <div className="w-8 h-8 rounded bg-orange-500 text-white font-black flex items-center justify-center text-sm shrink-0">{i + 1}</div>
                  <span className="text-sm text-gray-700 font-medium pt-1">{tt(p)}</span>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start space-y-5">
            <div className="bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg p-6 text-white">
              <h4 className="font-black text-xl">{tt('Need This Service?')}</h4>
              <p className="text-green-100 text-sm mt-2">{tt('Get a free quote within 24 hours from our experts.')}</p>
              <Link to="/get-quote" className="inline-flex items-center gap-2 mt-5 bg-white text-green-700 font-bold px-5 py-3 rounded text-xs uppercase tracking-wider hover:bg-orange-500 hover:text-white transition-colors">{tt('Get a Quote')} <Icons.ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="font-bold text-[#0e1116]">{tt('Related Services')}</h4>
              <ul className="mt-4 space-y-3">
                {related.map(r => (
                  <li key={r.slug}><Link to={`/services/${r.slug}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-green-600"><Icons.ChevronRight className="w-4 h-4 text-orange-500" /> {tt(r.title)}</Link></li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0e1116] text-white rounded-lg p-6">
              <Icons.Headphones className="w-8 h-8 text-orange-400" />
              <h4 className="font-black text-lg mt-3">{tt('Talk to an Expert')}</h4>
              <p className="text-gray-400 text-sm mt-1">{tt('Available 24/7 for consultation.')}</p>
              <a href="tel:+17866732523" className="block mt-3 text-green-400 font-bold">(+1) 786-673-2523</a>
              <a href="tel:+919960250279" className="block mt-1 text-green-400 font-bold">+91 9960 250 279</a>
            </div>
          </aside>
        </div>
      </section>

      {kw && (
        <section className="py-16 bg-[#0e1116] text-white relative overflow-hidden">
          <ServiceBackdrop slug={s.slug} accent="#94a3b8" accentAlt="#f97316" />
          <div className="relative max-w-[1100px] mx-auto px-6">
            <SectionLabel>Sub-Services & Multilingual Keywords</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black">
              {s.title} <span className="text-orange-500">in Every Language</span>
            </h2>
            <p className="text-gray-400 mt-3 max-w-2xl">Discover our complete {s.title.toLowerCase()} sub-services and how clients reach us across {langCodes.length} languages worldwide.</p>

            {/* Sub-services */}
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-green-400 font-bold mb-3 flex items-center gap-2">
                <Icons.Layers className="w-3.5 h-3.5" /> Specialised sub-services
              </div>
              <div className="flex flex-wrap gap-2">
                {kw.subServices.map(sub => (
                  <span key={sub} className="inline-flex items-center gap-1.5 text-xs bg-gray-900/40 border border-gray-700/50 text-green-100 px-3 py-1.5 rounded-full">
                    <Icons.CheckCircle2 className="w-3 h-3 text-orange-400" /> {sub}
                  </span>
                ))}
              </div>
            </div>

            {/* Buyer-intent English keywords */}
            {intentKeywords.length > 0 && (
              <div className="mt-8" data-testid="service-intent-keywords">
                <div className="text-[10px] uppercase tracking-[0.2em] text-green-400 font-bold mb-3 flex items-center gap-2">
                  <Icons.Search className="w-3.5 h-3.5" /> Search terms we target for {s.title}
                </div>
                <div className="flex flex-wrap gap-2">
                  {intentKeywords.map(kwd => (
                    <span key={kwd} className="text-xs bg-[#1a1f2e] border border-gray-800 text-gray-300 px-3 py-1.5 rounded-full hover:border-green-600 hover:text-green-300 transition-colors">
                      {kwd}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Multilingual keywords */}
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-3 flex items-center gap-2">
                <Icons.Globe2 className="w-3.5 h-3.5" /> Search terms in {langCodes.length} languages
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {langCodes.map(lc => (
                  <div key={lc} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-3" dir={lc === 'ar' ? 'rtl' : 'ltr'}>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider mb-1.5">
                      <span className="text-orange-400 font-bold">{lc}</span>
                      <span className="text-gray-500">{LANG_LABELS[lc]}</span>
                    </div>
                    <div className="text-sm text-green-100 font-medium leading-snug">{kw.t[lc] || kw.t.en}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hidden but indexable */}
            <div className="sr-only">
              {kw.name} services worldwide: {Object.values(kw.t).join(' · ')}. Sub-services include {kw.subServices.join(', ')}.
            </div>
          </div>
        </section>
      )}

      <WhyChooseUs />
      <CTA />
      <FloatingActions />
    </>
  );
};

export const PageHero = ({ title, highlight, typewriter, subtitle, breadcrumb }) => {
  const { tt } = useLang();
  return (
  <section className="relative bg-[#0e1116] text-white overflow-hidden py-24">
    <DotConnect density={70} color="#94a3b8" lineColor="rgba(148,163,184,0.85)" accentColor="#f97316" maxDistance={140} />
    <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(148,163,184,0.18),transparent_60%)] pointer-events-none"></div>
    <div className="relative max-w-[1400px] mx-auto px-6 text-center">
      <SectionLabel>{breadcrumb || 'Land Drafters'}</SectionLabel>
      <h1 className="text-5xl md:text-7xl font-black leading-[1.05]">{tt(title)} <span className="text-green-500">{tt(highlight)}</span>{typewriter && <><br /><Typewriter words={typewriter} className="text-orange-500" /></>}</h1>
      {subtitle && <p className="text-gray-400 mt-6 max-w-2xl mx-auto text-lg">{tt(subtitle)}</p>}
    </div>
  </section>
  );
};

export default ServicesIndex;
